import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SupplierFormFields, SupplierFormValues } from "./SupplierFormFields";

const supplierSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Informe o nome")
    .max(120, "Nome muito longo"),
  document: z.string().trim().max(30, "Documento muito longo").optional(),
  phone: z.string().trim().max(30, "Telefone muito longo").optional(),
  email: z
    .string()
    .trim()
    .max(255, "E-mail muito longo")
    .refine(
      (val) => !val || z.string().email().safeParse(val).success,
      "E-mail inválido"
    )
    .optional(),
});

export interface SupplierQuickCreateValues {
  name: string;
  document?: string;
  phone?: string;
  email?: string;
}

interface SupplierQuickCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (values: SupplierQuickCreateValues) => Promise<string | null>;
}

export function SupplierQuickCreateDialog({
  open,
  onOpenChange,
  onCreate,
}: SupplierQuickCreateDialogProps) {
  const [values, setValues] = useState<SupplierFormValues>({
    name: "",
    document: "",
    phone: "",
    email: "",
    chartAccountId: "__none__",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues({ name: "", document: "", phone: "", email: "", chartAccountId: "__none__" });
  }, [open]);

  const canSubmit = values.name.trim().length >= 2;

  const handleSubmit = async () => {
    const payload = {
      name: values.name.trim(),
      document: values.document.trim() || undefined,
      phone: values.phone.trim() || undefined,
      email: values.email.trim() || undefined,
    };

    const parsed = supplierSchema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Dados inválidos");
      return;
    }

    setSubmitting(true);
    try {
      const id = await onCreate(payload);
      if (id) onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cadastrar fornecedor/credor</DialogTitle>
          <DialogDescription>
            Cadastre rapidamente para poder selecionar no lançamento da despesa.
          </DialogDescription>
        </DialogHeader>

        <SupplierFormFields
          values={values}
          onChange={setValues}
          autoFocusName
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? "Salvando..." : "Cadastrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
